import {
  DeesElement,
  customElement,
  property,
  state,
  html,
  css,
  directives,
} from '@design.estate/dees-element';
import type {
  IKanbanColumn,
  IKanbanComment,
  IKanbanCreate,
  IKanbanMove,
  IKanbanPerson,
  IKanbanProvider,
  IKanbanTicket,
  IKanbanTicketDraft,
} from '../interfaces.js';
import type {
  TReferenceContent,
  TReferenceSearch,
} from '../../00group-input/dees-input-reference/interfaces.js';
import type { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { DeesKanbanTicketDetail } from '../dees-kanban-ticket-detail/dees-kanban-ticket-detail.js';
import { kanbanStyles } from '../kanban.styles.js';
import { kanbanError, kanbanEvent, sortKanbanTickets, ticketLabel } from '../kanban.helpers.js';
import { demoFunc } from './dees-kanban.demo.js';
import { KanbanDragController } from '../kanban.dragcontroller.js';
import '../dees-kanban-column/dees-kanban-column.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-kanban': DeesKanban;
  }
}

const interactionEvents = [
  'kanban-ticket-open',
  'kanban-ticket-move',
  'kanban-ticket-create',
  'kanban-move-menu',
  'kanban-ticket-save',
  'kanban-ticket-draft',
  'kanban-comment-draft',
  'kanban-comment-added',
  'kanban-comments-refresh',
  'kanban-detail-back',
] as const;

interface IKanbanInteractionMap {
  'kanban-ticket-open': { ticketId: string };
  'kanban-ticket-move': IKanbanMove;
  'kanban-ticket-create': Omit<IKanbanCreate, 'title'>;
  'kanban-move-menu': { ticketId: string; anchor: HTMLElement };
  'kanban-ticket-save': { ticketId: string; draft: IKanbanTicketDraft };
  'kanban-ticket-draft': { ticketId: string; draft?: IKanbanTicketDraft };
  'kanban-comment-draft': { ticketId: string; body: TReferenceContent };
  'kanban-comment-added': { comment: IKanbanComment; submittedBody: TReferenceContent };
  'kanban-comments-refresh': { ticketId: string };
  'kanban-detail-back': Record<string, never>;
}

type TKanbanInteraction = {
  [K in keyof IKanbanInteractionMap]: { type: K; detail: IKanbanInteractionMap[K] }
}[keyof IKanbanInteractionMap];

@customElement('dees-kanban')
export class DeesKanban extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Kanban'];

  @property({ type: String })
  accessor heading = 'Board';

  @property({ attribute: false })
  accessor tickets: IKanbanTicket[] = [];

  @property({ attribute: false })
  accessor columns: IKanbanColumn[] = [];

  @property({ attribute: false })
  accessor people: IKanbanPerson[] = [];

  @property({ attribute: false })
  accessor provider: IKanbanProvider = {};

  @property({ type: Boolean })
  accessor loading = false;

  @property({ type: String })
  accessor error = '';

  @state()
  private accessor query = '';

  @state()
  private accessor epicId = '';

  @state()
  private accessor operationError = '';

  @state()
  private accessor pendingTicketId = '';

  @state()
  private accessor openingTicket = false;

  private modal?: DeesModal;
  private modalOpening?: Promise<DeesModal>;
  private detail?: DeesKanbanTicketDetail;
  private selectedId = '';
  private history: string[] = [];
  private openAbort?: AbortController;
  private commentsAbort?: AbortController;
  private commentCache = new Map<string, IKanbanComment[]>();
  private commentRevisions = new Map<string, number>();
  private commentDrafts = new Map<string, TReferenceContent>();
  private ticketDrafts = new Map<string, IKanbanTicketDraft>();
  private resolvedTickets = new Map<string, IKanbanTicket>();
  private activeMove?: symbol;
  private readonly dragController = new KanbanDragController(
    this,
    input => this.persistMove(input),
    () => !this.pendingTicketId,
  );

  public static styles = [
    ...kanbanStyles,
    css`
      :host {
        padding: 20px 24px;
        background: var(--dees-color-bg-canvas);
        min-height: 100%;
      }

      .heading {
        display: flex;
        gap: 12px;
        align-items: center;
        flex-wrap: wrap;
        margin-bottom: 16px;
      }

      .identity {
        display: flex;
        gap: 12px;
        align-items: baseline;
        flex: 1;
        min-width: 220px;
        flex-wrap: wrap;
      }

      h2 {
        font-size: var(--dees-font-title3-size);
        font-weight: 600;
        letter-spacing: -.02em;
        line-height: 1.3;
        overflow-wrap: anywhere;
      }

      .totals {
        font-size: var(--dees-font-caption1-size);
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
      }

      .filters {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .filters dees-input-text {
        width: 230px;
        min-width: 140px;
      }

      .filters dees-input-dropdown {
        width: 180px;
        min-width: 140px;
      }

      .actions {
        display: flex;
        gap: 8px;
        margin-left: auto;
      }

      .columns {
        display: flex;
        gap: 20px;
        overflow-x: auto;
        padding: 3px 2px 16px;
        align-items: stretch;
      }

      @media (max-width: 1100px) {
        .filters {
          order: 3;
          flex-basis: 100%;
        }
        .filters dees-input-text {
          flex: 1;
          max-width: 320px;
        }
      }

      @media (max-width: 600px) {
        :host {
          padding: 12px 16px;
        }
        .heading {
          gap: 12px 8px;
        }
        .identity {
          min-width: 100%;
          gap: 6px 10px;
        }
        .actions {
          margin-left: 0;
        }
        .filters {
          flex-wrap: wrap;
        }
        .filters dees-input-text,
        .filters dees-input-dropdown {
          flex: 1;
          width: auto;
          min-width: 130px;
        }
        .columns {
          margin: 0 -16px;
          padding: 3px 16px 16px;
          scroll-snap-type: x proximity;
          scroll-padding-inline: 16px;
          gap: 14px;
        }
        dees-kanban-column {
          scroll-snap-align: start;
          flex: 0 0 86%;
          min-width: 0;
          max-width: none;
        }
      }
    `,
  ];

  public async connectedCallback(): Promise<void> {
    const connection = super.connectedCallback();
    for (const name of interactionEvents) this.addEventListener(name, this.handleInteraction);
    this.dragController.connect();
    await connection;
  }

  public updated(): void {
    this.syncDetail();
    this.dragController.sync();
  }

  public render() {
    const epics = sortKanbanTickets(this.tickets.filter(ticket => ticket.kind === 'epic'));
    const query = this.query.trim().toLocaleLowerCase();
    const tasks = this.tickets.filter(ticket =>
      ticket.kind === 'task' &&
      (!this.epicId || ticket.parentId === this.epicId) &&
      (!query ||
        [
          ticket.key,
          ticket.title,
          ticket.assignee?.name,
          ...ticket.labels ?? [],
          ...this.tickets
            .filter(child => child.parentId === ticket.id)
            .map(child => `${child.key} ${child.title}`),
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(query)),
    );
    const epic = epics.find(ticket => ticket.id === this.epicId);
    return html`<header class="heading">
      <div class="identity">
        <h2>${this.heading}</h2>
        <span class="totals">${tasks.length} tasks · ${epics.length} epics</span>
      </div>
      <div class="filters">
        <dees-input-text
          label="Search tickets"
          .labelPosition=${'none'}
          placeholder="Search tickets…"
          .value=${this.query}
          @input=${(event: Event) => {
            this.query = String((event.currentTarget as DeesInputText).value ?? '');
          }}
        ></dees-input-text>
        <dees-input-dropdown
          label="Epic"
          .labelPosition=${'none'}
          .value=${{ key: this.epicId, option: epic?.title ?? 'All epics' }}
          .options=${[
            { key: '', option: 'All epics' },
            ...epics.map(ticket => ({ key: ticket.id, option: ticket.title })),
          ]}
          @selectedOption=${(event: CustomEvent<{ key: string }>) => {
            this.epicId = event.detail.key;
          }}
        ></dees-input-dropdown>
        ${epic
          ? html`<dees-button
              type="ghost"
              text="Open epic"
              @clicked=${() => {
                void this.openTicket(epic.id);
              }}
            ></dees-button>`
          : ''}
      </div>
      ${this.provider.createTicket
        ? html`<div class="actions">
            <dees-button
              type="secondary"
              text="New epic"
              .disabled=${!this.columns.length}
              @clicked=${() => {
                void this.createTicket({ kind: 'epic', columnId: this.columns[0].id });
              }}
            ></dees-button>
            <dees-button
              type="accent"
              icon="lucide:Plus"
              text="New task"
              .disabled=${!this.columns.length}
              @clicked=${() => {
                void this.createTicket({
                  kind: 'task',
                  columnId: this.columns[0].id,
                  parentId: this.epicId || undefined,
                });
              }}
            ></dees-button>
          </div>`
        : ''}
    </header>
      ${this.loading || this.openingTicket
        ? html`<p class="caption" role="status">${this.loading
            ? 'Loading board…'
            : 'Opening ticket…'}</p>`
        : ''}
      ${this.error || this.operationError
        ? html`<p class="error" role="alert">${this.error || this.operationError}</p>`
        : ''}
      ${!this.columns.length
        ? html`<p class="empty">No columns configured.</p>`
        : html`<div class="columns" aria-label=${this.heading}>
            ${directives.repeat(this.columns, column => column.id, column => html`<dees-kanban-column
              .column=${column}
              .columns=${this.columns}
              .pointerManaged=${true}
              .tickets=${tasks.filter(ticket => ticket.columnId === column.id)}
              .allTickets=${this.tickets}
              .movable=${Boolean(this.provider.moveTicket)}
              .canCreate=${Boolean(this.provider.createTicket)}
              .movesDisabled=${Boolean(this.pendingTicketId)}
              .pendingTicketId=${this.pendingTicketId}
            ></dees-kanban-column>`)}
          </div>`}`;
  }

  private findTicket(id: string): IKanbanTicket | undefined {
    return this.tickets.find(ticket => ticket.id === id) ?? this.resolvedTickets.get(id);
  }

  private searchTickets: TReferenceSearch = async (query, signal) => {
    const tickets = this.provider.searchTickets
      ? await this.provider.searchTickets(query, signal)
      : this.tickets.filter(ticket =>
          ticketLabel(ticket).toLocaleLowerCase().includes(query.toLocaleLowerCase()),
        );
    if (!signal.aborted) for (const ticket of tickets) this.resolvedTickets.set(ticket.id, ticket);
    return tickets.map(ticket => ({ id: ticket.id, label: ticketLabel(ticket) }));
  };

  /** Open a task, epic, subtask or remotely resolved linked ticket in the board's detail modal. */
  public async openTicket(id: string, addHistory = true): Promise<void> {
    this.openAbort?.abort();
    const controller = this.openAbort = new AbortController();
    this.openingTicket = true;
    this.operationError = '';
    try {
      const ticket = this.provider.getTicket
        ? await this.provider.getTicket(id, controller.signal)
        : this.findTicket(id);
      if (controller.signal.aborted || !this.isConnected) return;
      if (!ticket || ticket.id !== id)
        throw new Error('This ticket is unavailable or you do not have access.');
      this.resolvedTickets.set(ticket.id, ticket);
      this.tickets = this.tickets.map(item => item.id === ticket.id ? ticket : item);
      if (addHistory && this.selectedId && this.selectedId !== id && this.modal)
        this.history.push(this.selectedId);
      this.selectedId = id;
      if (!this.detail) {
        this.detail = new DeesKanbanTicketDetail();
        this.detail.showTicketKey = false;
        for (const name of interactionEvents) this.detail.addEventListener(name, this.handleInteraction);
      }
      this.detail.ticket = ticket;
      this.detail.error = '';
      this.syncDetail();
      if (!this.modal && !this.modalOpening) {
        this.modalOpening = DeesModal.createAndShow({
          heading: ticket.key,
          width: 1000,
          maxWidth: 1040,
          mobileFullscreen: true,
          contentPadding: 0,
          content: html`${this.detail}`,
          menuOptions: [],
        });
        const modal = await this.modalOpening;
        this.modalOpening = undefined;
        if (!this.isConnected) {
          await modal.destroy();
          return;
        }
        this.modal = modal;
        modal.addEventListener('modal-close', () => {
          if (this.modal !== modal) return;
          this.modal = undefined;
          this.selectedId = '';
          this.history = [];
          this.commentsAbort?.abort();
          this.openAbort?.abort();
        }, { once: true });
      } else if (this.modalOpening) await this.modalOpening;
      this.syncDetail();
      if (controller.signal.aborted) return;
      kanbanEvent(this, 'kanban-ticket-opened', { ticketId: id });
      await this.refreshComments(id);
    } catch (error) {
      if (!controller.signal.aborted) this.reportError(error);
    } finally {
      if (this.openAbort === controller) this.openingTicket = false;
    }
  }

  private syncDetail(): void {
    if (!this.detail || !this.selectedId) return;
    const ticket = this.resolvedTickets.get(this.selectedId) ?? this.findTicket(this.selectedId);
    if (!ticket) return;
    // Host-fed updates take precedence for records on this board.
    this.detail.ticket = this.tickets.find(item => item.id === this.selectedId) ?? ticket;
    this.detail.tickets = [
      ...this.tickets,
      ...[...this.resolvedTickets.values()].filter(
        item => !this.tickets.some(known => known.id === item.id),
      ),
    ];
    this.detail.columns = this.columns;
    this.detail.people = this.people;
    this.detail.provider = this.provider;
    this.detail.searchTickets = this.searchTickets;
    this.detail.comments = this.commentCache.get(this.selectedId) ?? [];
    this.detail.commentDraft = this.commentDrafts.get(this.selectedId) ?? [];
    this.detail.draft = this.ticketDrafts.get(this.selectedId);
    this.detail.canGoBack = this.history.length > 0;
    this.detail.saving = this.pendingTicketId === this.selectedId;
    if (this.modal) this.modal.heading = this.detail.ticket.key;
  }

  /** Replace a ticket's discussion with an authoritative snapshot (for realtime updates). */
  public setComments(ticketId: string, comments: IKanbanComment[]): void {
    if (comments.some(comment => comment.ticketId !== ticketId))
      throw new Error('Comment ticket IDs must match.');
    this.commentCache.set(ticketId, [...comments]);
    this.commentRevisions.set(ticketId, (this.commentRevisions.get(ticketId) ?? 0) + 1);
    this.syncDetail();
  }

  public async refreshComments(ticketId = this.selectedId): Promise<void> {
    this.commentsAbort?.abort();
    const controller = this.commentsAbort = new AbortController();
    if (!ticketId || !this.provider.loadComments) {
      if (this.detail) {
        this.detail.loadingComments = false;
        this.detail.commentsError = '';
      }
      return;
    }
    const revision = this.commentRevisions.get(ticketId) ?? 0;
    const existingIds = new Set((this.commentCache.get(ticketId) ?? []).map(comment => comment.id));
    if (this.detail && ticketId === this.selectedId) {
      this.detail.loadingComments = true;
      this.detail.commentsError = '';
    }
    try {
      const comments = await this.provider.loadComments(ticketId, controller.signal);
      if (!controller.signal.aborted && (this.commentRevisions.get(ticketId) ?? 0) === revision) {
        const postedWhileLoading = (this.commentCache.get(ticketId) ?? []).filter(
          comment => !existingIds.has(comment.id) && !comments.some(item => item.id === comment.id),
        );
        this.setComments(ticketId, [...comments, ...postedWhileLoading]);
      }
    } catch (error) {
      if (!controller.signal.aborted && this.detail && ticketId === this.selectedId)
        this.detail.commentsError = kanbanError(error);
    } finally {
      if (!controller.signal.aborted && this.detail && ticketId === this.selectedId)
        this.detail.loadingComments = false;
    }
  }

  private mergeTickets(tickets: IKanbanTicket[], addToBoard = false): void {
    for (const ticket of tickets) this.resolvedTickets.set(ticket.id, ticket);
    this.tickets = this.tickets.map(ticket => tickets.find(item => item.id === ticket.id) ?? ticket);
    if (addToBoard)
      this.tickets = [
        ...this.tickets,
        ...tickets.filter(ticket => !this.tickets.some(item => item.id === ticket.id)),
      ];
    this.syncDetail();
    kanbanEvent(this, 'kanban-change', { tickets });
  }

  /** Provider commits the move and supplies the new ordering. Hierarchy is never part of a move request. */
  public async moveTicket(input: IKanbanMove): Promise<void> {
    if (this.dragController.busy) return;
    await this.persistMove(input, true);
  }

  private async persistMove(input: IKanbanMove, animate = false): Promise<boolean> {
    const ticket = this.findTicket(input.ticketId);
    if (
      !ticket ||
      !this.provider.moveTicket ||
      ticket.permissions?.move === false ||
      this.pendingTicketId ||
      input.beforeTicketId === ticket.id
    )
      return false;
    if (!this.columns.some(column => column.id === input.columnId)) {
      this.reportError(new Error('Unknown destination column.'));
      return false;
    }
    const before = input.beforeTicketId ? this.findTicket(input.beforeTicketId) : undefined;
    if (
      input.beforeTicketId &&
      (!before || before.columnId !== input.columnId || before.kind !== ticket.kind)
    ) {
      this.reportError(new Error('Invalid insertion target.'));
      return false;
    }
    const operation = this.activeMove = Symbol();
    this.pendingTicketId = ticket.id;
    this.operationError = '';
    try {
      const changed = await this.provider.moveTicket(input);
      if (this.activeMove !== operation) return false;
      if (!changed.some(item => item.id === ticket.id))
        throw new Error('The move provider did not return the moved ticket.');
      const positions = animate ? this.dragController.capture() : undefined;
      this.mergeTickets(changed);
      if (positions) {
        await this.updateComplete;
        await this.dragController.animateFrom(positions);
      }
      return true;
    } catch (error) {
      if (this.activeMove === operation) this.reportError(error);
      return false;
    } finally {
      if (this.activeMove === operation) {
        this.activeMove = undefined;
        this.pendingTicketId = '';
        this.syncDetail();
      }
    }
  }

  private async saveTicket(ticketId: string, draft: IKanbanTicketDraft): Promise<void> {
    const ticket = this.findTicket(ticketId);
    if (
      !ticket ||
      !this.provider.updateTicket ||
      ticket.permissions?.edit === false ||
      this.pendingTicketId ||
      !draft.title.trim()
    )
      return;
    const parent = draft.parentId ? this.findTicket(draft.parentId) : undefined;
    if (
      (ticket.kind === 'subtask' && parent?.kind !== 'task') ||
      (ticket.kind === 'task' && draft.parentId && parent?.kind !== 'epic')
    ) {
      this.reportError(new Error('Choose a valid parent ticket.'));
      return;
    }
    const assignee =
      this.people.find(person => person.id === draft.assigneeId) ??
      (ticket.assignee?.id === draft.assigneeId ? ticket.assignee : undefined);
    if (draft.assigneeId && !assignee) {
      this.reportError(new Error('Choose an available assignee.'));
      return;
    }
    this.pendingTicketId = ticketId;
    this.operationError = '';
    if (this.detail) this.detail.error = '';
    try {
      const saved = await this.provider.updateTicket({
        ticketId,
        changes: {
          title: draft.title.trim(),
          description: draft.description,
          priority: draft.priority,
          assignee: assignee ?? null,
          parentId: ticket.kind === 'epic' ? null : draft.parentId || null,
        },
      });
      if (saved.id !== ticketId)
        throw new Error('The update provider returned a different ticket.');
      this.ticketDrafts.delete(ticketId);
      this.mergeTickets([saved]);
      if (this.selectedId === ticketId) this.detail?.finishEditing();
    } catch (error) {
      this.reportError(error);
    } finally {
      this.pendingTicketId = '';
      this.syncDetail();
    }
  }

  private async createTicket(input: Omit<IKanbanCreate, 'title'>): Promise<void> {
    if (!this.provider.createTicket || !this.columns.some(column => column.id === input.columnId))
      return;
    const parent = input.parentId ? this.findTicket(input.parentId) : undefined;
    if (parent?.permissions?.createChild === false) return;
    if (
      (input.kind === 'subtask' && parent?.kind !== 'task') ||
      (input.kind === 'task' && parent && parent.kind !== 'epic')
    )
      return;
    const parentId =
      input.parentId ?? (input.kind === 'task' ? this.epicId || undefined : undefined);
    await DeesModal.prompt({
      heading: `New ${input.kind}`,
      label: 'Title',
      confirmLabel: 'Create',
      onConfirm: async title => {
        const ticket = await this.provider.createTicket!({ ...input, parentId, title });
        this.mergeTickets([ticket], true);
      },
    });
  }

  private async openMoveMenu(ticketId: string, anchor: HTMLElement): Promise<void> {
    const ticket = this.findTicket(ticketId);
    if (
      !ticket ||
      !this.provider.moveTicket ||
      ticket.permissions?.move === false ||
      this.pendingTicketId
    )
      return;
    const siblings = sortKanbanTickets(
      this.tickets.filter(item => item.columnId === ticket.columnId && item.kind === ticket.kind),
    );
    const index = siblings.findIndex(item => item.id === ticket.id);
    const rect = anchor.getBoundingClientRect();
    await DeesContextmenu.openContextMenuWithOptions(
      new MouseEvent('click', { clientX: rect.right, clientY: rect.bottom }),
      [
        {
          name: 'Move up',
          disabled: index <= 0,
          action: async () => {
            if (index > 0)
              await this.moveTicket({
                ticketId,
                columnId: ticket.columnId,
                beforeTicketId: siblings[index - 1].id,
              });
          },
        },
        {
          name: 'Move down',
          disabled: index < 0 || index >= siblings.length - 1,
          action: async () => {
            if (index >= 0 && index < siblings.length - 1)
              await this.moveTicket({
                ticketId,
                columnId: ticket.columnId,
                beforeTicketId: siblings[index + 2]?.id,
              });
          },
        },
        { divider: true },
        ...this.columns.filter(column => column.id !== ticket.columnId).map(column => ({
          name: `Move to ${column.title}`,
          action: async () => {
            await this.moveTicket({ ticketId, columnId: column.id });
          },
        })),
      ],
      { ownerElement: anchor },
    );
  }

  private reportError(error: unknown): void {
    this.operationError = kanbanError(error);
    if (this.detail && this.modal) this.detail.error = this.operationError;
    kanbanEvent(this, 'kanban-error', { error, message: this.operationError });
  }

  private handleInteraction = (event: Event): void => {
    event.stopPropagation();
    const { type, detail } = event as unknown as TKanbanInteraction;
    switch (type) {
      case 'kanban-ticket-open':
        void this.openTicket(detail.ticketId);
        break;
      case 'kanban-ticket-move':
        void this.moveTicket(detail);
        break;
      case 'kanban-ticket-create':
        void this.createTicket(detail).catch(error => this.reportError(error));
        break;
      case 'kanban-move-menu':
        void this.openMoveMenu(detail.ticketId, detail.anchor).catch(error =>
          this.reportError(error),
        );
        break;
      case 'kanban-ticket-save':
        void this.saveTicket(detail.ticketId, detail.draft);
        break;
      case 'kanban-ticket-draft':
        if (detail.draft) this.ticketDrafts.set(detail.ticketId, detail.draft);
        else this.ticketDrafts.delete(detail.ticketId);
        this.syncDetail();
        break;
      case 'kanban-comment-draft':
        this.commentDrafts.set(detail.ticketId, detail.body);
        break;
      case 'kanban-comment-added': {
        const { comment, submittedBody } = detail;
        const comments = this.commentCache.get(comment.ticketId) ?? [];
        this.commentCache.set(comment.ticketId, [
          ...comments.filter(item => item.id !== comment.id),
          comment,
        ]);
        if (
          JSON.stringify(this.commentDrafts.get(comment.ticketId) ?? []) ===
          JSON.stringify(submittedBody)
        )
          this.commentDrafts.delete(comment.ticketId);
        this.syncDetail();
        kanbanEvent(this, 'kanban-comment-saved', { comment });
        break;
      }
      case 'kanban-comments-refresh':
        void this.refreshComments(detail.ticketId);
        break;
      case 'kanban-detail-back': {
        const previous = this.history.pop();
        if (previous) void this.openTicket(previous, false);
        break;
      }
    }
  };

  public async disconnectedCallback(): Promise<void> {
    for (const name of interactionEvents) this.removeEventListener(name, this.handleInteraction);
    this.dragController.disconnect();
    if (this.activeMove) {
      this.activeMove = undefined;
      this.pendingTicketId = '';
    }
    this.openAbort?.abort();
    this.commentsAbort?.abort();
    void this.modal?.destroy();
    await super.disconnectedCallback();
  }
}
